import React from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {dashBoardStyle} from "features/Dashboard/components/dashBoardStyle";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import {useAppSelector} from "../../../../redux/store";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import Link from "@mui/material/Link";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import ConstCenterVerfication from "../CostCenterVerfication/CostCenterVerification";
import IndividualPaymentLimitsReportDialog from "components/Dialogs/IndividualPaymentLimitsReportDialog";
import {dashBoardMiscstyle} from "./dashBoardMiscstyle";
import {convertTheLink} from "commonUtils/utils";
import {
  Alerts,
  AxiosErrorCode,
  CPSConstants,
  CPSMessages,
  HttpStatusCode,
  PageNames,
} from "commonUtils/enumerations";
import {
  HttpException,
  ServiceAuthenticationException,
} from "services/exceptionHandlerService";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import {logMessage} from "services/loggerService";
import {OLD_CPS_URL} from "commonUtils/globalAPISettings";
import "../../../Dashboard/Dashboard.css";

const DashboardMiscellaneous: React.FC<any> = miscData => {
  const {authDetails, userDetails} = useAppSelector(state => {
    return state.auth;
  });

  const [reportopen, setReportOpen] = React.useState<boolean>(false);
  const [errorMsg, setErrorMsg] = React.useState<string>("");

  const handleOpenReportDialog = () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} has opened the Individual Payment Limits Report dialog.`,
        PageNames.DASHBOARD,
      );
      setReportOpen(true);
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  };

  const handleClickARF = () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} has clicked the 'Click Here to Create ARF Form' link.`,
        PageNames.DASHBOARD,
      );
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  };

  const handleClickOrf = () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} has clicked the 'Create Ownership Requests' link.`,
        PageNames.LOGIN,
      );
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  };

  const handleAxiosErrors = (error: any) => {
    const data = {pagename: PageNames.DASHBOARD};
    if (error?.code === AxiosErrorCode.ERR_BAD_REQUEST) {
      if (
        error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.response?.status === HttpStatusCode.FORBIDDEN
      ) {
        ServiceAuthenticationException(error, data, error?.response?.status);
      } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      } else if (
        error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
      ) {
        HttpException(error, data);
        setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      } else {
        setErrorMsg(error.message);
      }
    } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
      setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
      setErrorMsg(CPSMessages.NETWORK_ERROR);
    } else {
      setErrorMsg(error.message);
    }
  };

  return (
    <Paper className="paper-look">
      {reportopen && (
        <IndividualPaymentLimitsReportDialog
          reportopen={reportopen}
          setOpen={setReportOpen}
          buttononename="Export to Excel"
          buttontwoname="Close"
        />
      )}
      {errorMsg && (
        <FormValidationAlert severity={Alerts.ERROR} message={errorMsg} />
      )}
      <Typography component="h1" className="alert-heading">
        <MiscellaneousServicesOutlinedIcon className="outline-icon" />
        Miscellaneous
      </Typography>
      <Box className="my-border" />
      <Box className="box-padding-bottom" />
      <List className="miscellaneous-my-list miscellaneous-alert-block">
        {miscData?.miscData?.permissionToViewPaymentLimitsReport && (
          <>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <>
                    <Typography component="span" className="alert-count">
                      Report
                    </Typography>
                  </>
                }
                secondary={
                  <>
                    <Link
                      sx={dashBoardMiscstyle.cusrsorPointer}
                      onClick={handleOpenReportDialog}>
                      <Typography
                        component="span"
                        className="individual-payment-limit">
                        Individual Payment Limits Report
                      </Typography>
                    </Link>
                  </>
                }
              />
            </ListItem>
            <Divider />
          </>
        )}

        {miscData?.miscData?.permissionToViewARF && (
          <>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <>
                    <Typography
                      component="h1"
                      className="advising-request-form">
                      Advising Request Form
                    </Typography>
                  </>
                }
                secondary={
                  <>
                    <Link
                      href={convertTheLink(
                        `${OLD_CPS_URL}${miscData?.miscData?.arfUrl}&`,
                        authDetails,
                        userDetails,
                      )}
                      onClick={handleClickARF}
                      target="_blank">
                      <Typography
                        component="span"
                        className="individual-payment-limit">
                        Click Here to Create ARF Form
                      </Typography>
                    </Link>
                  </>
                }
              />
            </ListItem>
            <Divider />
          </>
        )}

        {miscData?.miscData?.permissionToViewORF && (
          <>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <>
                    <Typography
                      component="span"
                      className="advising-request-form">
                      Advisor/Speaker Alignment Requests
                    </Typography>
                  </>
                }
                secondary={
                  <React.Fragment>
                    <Stack spacing={1}>
                      <Link
                        href={convertTheLink(
                          `${OLD_CPS_URL}${miscData?.miscData?.orfUrl}&`,
                          authDetails,
                          userDetails,
                        )}
                        onClick={handleClickOrf}
                        target="_blank">
                        <Typography
                          component="span"
                          className="individual-payment-limit"
                          display="block">
                          Create Alignment Requests
                        </Typography>
                      </Link>
                      <Link
                        href={`${CPSConstants.APPENDVIEWOWNERSHIP}`}
                        onClick={handleClickOrf}>
                        <Typography
                          component="span"
                          className="individual-payment-limit"
                          display="block">
                          View Alignment Requests
                        </Typography>
                      </Link>
                    </Stack>
                  </React.Fragment>
                }
              />
            </ListItem>
            <Divider />
          </>
        )}

        <ListItem alignItems="flex-start">
          <ConstCenterVerfication />
        </ListItem>
        <Divider />
      </List>
    </Paper>
  );
};
export default DashboardMiscellaneous;
